import {
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInCalendarWeeks,
  differenceInCalendarYears,
} from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { forwardRef, useMemo } from 'react';
import { FormattedRelativeTime } from 'react-intl';
import { Props as FormattedRelativeTimeProps } from 'react-intl/lib/src/components/relative';
import { styled } from 'styled-components';

import { Badge } from '@src/components/atoms/badge';
import { useCmsContent } from '@src/contexts/cms-content-context';
import { timeService } from '@src/services';

import { CardBadgeProps } from './types';

const CardBadge = forwardRef<HTMLDivElement, CardBadgeProps>(
  ({ arrivalDate, tripStatus, timeZone, className, from = new Date() }, ref) => {
    const content = useCmsContent();
    const isOnGoingTrip = tripStatus === 'InHouse';
    const isArrivalDateInTheFuture = timeService.isFuture(arrivalDate);

    const relativeTimeProps = useMemo<FormattedRelativeTimeProps>(() => {
      const targetDate = zonedTimeToUtc(arrivalDate, timeZone);

      const intervalInYears = differenceInCalendarYears(targetDate, from);
      const intervalInMonths = differenceInCalendarMonths(targetDate, from);
      const intervalInWeeks = differenceInCalendarWeeks(targetDate, from);
      const intervalInDays = differenceInCalendarDays(targetDate, from);

      if (intervalInYears > 0 && intervalInMonths > 6) {
        return {
          unit: 'year',
          value: intervalInYears,
        };
      }

      if (intervalInMonths > 0 && intervalInWeeks > 3) {
        return {
          unit: 'month',
          value: intervalInMonths,
        };
      }

      if (intervalInWeeks > 2 && intervalInDays > 14) {
        return {
          unit: 'week',
          value: intervalInWeeks,
        };
      }

      return {
        unit: 'day',
        value: intervalInDays,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [arrivalDate]);

    return (
      <Badge ref={ref} className={className}>
        <span>
          {isOnGoingTrip || !isArrivalDateInTheFuture ? (
            <>{content.current}</>
          ) : (
            <FormattedRelativeTime {...relativeTimeProps} numeric="auto" />
          )}
        </span>
      </Badge>
    );
  },
);
CardBadge.displayName = 'CardBadge';
export default styled(CardBadge)``;
